/**
 * Utility functions for GIF handling
 */

/**
 * Checks if a URL or file path points to a GIF file
 * @param src - The source URL or file path
 * @returns true if the source is a GIF file
 */
export const isGifFile = (src: string): boolean => {
  if (!src) return false

  // Check file extension
  const extension = src.toLowerCase().split('.').pop()
  if (extension === 'gif') {
    return true
  }

  // Check for GIF in URL path (even without extension)
  const urlPath = src.toLowerCase()
  if (urlPath.includes('.gif') || urlPath.includes('gif')) {
    return true
  }

  return false
}

/**
 * Checks if a URL is a data URL containing a GIF
 * @param src - The source URL
 * @returns true if the source is a data URL with GIF data
 */
export const isGifDataUrl = (src: string): boolean => {
  if (!src || !src.startsWith('data:')) return false

  // Check for GIF MIME type in data URL
  return src.includes('data:image/gif') || src.includes('image/gif')
}

/**
 * Determines if an image source should be rendered as a GIF
 * @param src - The image source URL
 * @returns true if the source should be rendered as a GIF
 */
export const shouldRenderAsGif = (src: string): boolean => {
  return isGifFile(src) || isGifDataUrl(src)
}

/**
 * Extracts dimensions from a GIF source for proper canvas sizing
 * @param src - The GIF source URL
 * @returns Promise resolving to width and height, or null if extraction fails
 */
export const getGifDimensions = async (
  src: string,
): Promise<{ width: number; height: number } | null> => {
  try {
    const response = await fetch(src)
    if (!response.ok) return null

    const arrayBuffer = await response.arrayBuffer()
    const uint8Array = new Uint8Array(arrayBuffer)

    // Parse GIF header to get dimensions
    if (uint8Array.length < 10) return null

    // GIF header: 6 bytes signature + 2 bytes width + 2 bytes height
    const width = uint8Array[6] | (uint8Array[7] << 8)
    const height = uint8Array[8] | (uint8Array[9] << 8)

    return { width, height }
  } catch (error) {
    console.warn('Failed to extract GIF dimensions:', error)
    return null
  }
}
